import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Scroll from 'react-scroll';

import { Forms, Modal, SaveButton, SystemText, TitleBar } from 'e4shared';

import { update, clearError } from 'actions/blog';
import Files from 'components/admin/forms/files';
import { addImage, toggleFormHelp } from 'actions/ui';

import config from 'config';
import { uploadType } from 'helpers/azure';

export const ManagePost = ({ show, close, reload, post: postProps }) => {
    const dispatch = useDispatch();

    const downlineCompetencies = useSelector(state => state.user.downlineCompetencies);
    const handles = useSelector(state => state.ui.handles);
    const images = useSelector(state => state.ui.images);
    const tags = useSelector(state => state.ui.tags);
    const user = useSelector(state => state.user.user);

    const [saving, setSaving] = useState(false);
    const [assets, setAssets] = useState([]);
    const [blogImage, setBlogImage] = useState('');
    const [blogImageMeta, setBlogImageMeta] = useState(null);
    const [competencies, setCompetencies] = useState([]);
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(new Date());
    const [errors, setErrors] = useState({});
    const [ownerId, setOwnerId] = useState(user?.id);
    const [postBody, setPostBody] = useState(postProps?.post);
    const [published, setPublished] = useState(false);
    const [title, setTitle] = useState('');
    const [warnings, setWarnings] = useState({});
    const [warningsShown, setWarningsShown] = useState({});

    useEffect(() => {
        if (postProps) {
            setAssets(postProps.assets);
            setBlogImage(postProps.blog_Image);
            setCompetencies(postProps.competency_Ids);
            setDate(postProps.date);
            setDescription(postProps.description);
            setOwnerId(postProps.owner_id);
            setPublished(postProps.published);
            setPostBody(postProps.post);
            setTitle(postProps.title);
        }
    }, [postProps]);

    const setErrorsAndWarnings = ({ errors, warnings, shownWarnings }) => {
        setErrors(errors);
        setWarnings(warnings);
        setWarningsShown(shownWarnings);
    };

    const validate = post => {
        let result = true;
        const errors = {};
        const warnings = {};
        const shownWarnings = warningsShown || {};

        if (!post?.title || post?.title?.length === 0) {
            errors.title = SystemText('BlogPost/ManagePost/Fields/Title/Error');
            result = false;
        }

        if (!post?.date || post?.date?.length === 0 || post?.date === '1970-01-01') {
            errors.date = SystemText('BlogPost/ManagePost/Fields/Date/Error');
            result = false;
        }

        if ((!post?.competency_Ids || post?.competency_Ids?.length === 0) && !shownWarnings?.competency_Ids) {
            warnings.competency_Ids = SystemText('BlogPost/ManagePost/Fields/Competency/Error');
            shownWarnings.competency_Ids = true;
            result = false;
        }

        setErrorsAndWarnings({ errors, warnings, shownWarnings });

        if (!result) document.getElementsByClassName('modal')[0].scrollTop = 0;

        return result;
    };

    const save = () => {
        const postToSave = {
            ...postProps,
            assets,
            owner_id: ownerId,
            title,
            date,
            description,
            post: postBody,
            blog_Image: blogImage,
            blog_ImageMeta: blogImageMeta,
            competency_Ids: competencies,
            published,
        };

        if (validate(postToSave)) {
            dispatch(
                update(postToSave, () => {
                    closeModal();
                })
            );
        } else {
            setSaving(false);
        }
    };

    const closeModal = () => {
        dispatch(clearError());
        setAssets(null);
        setBlogImage(null);
        setCompetencies(null);
        setDate(null);
        setDescription(null);
        setOwnerId(null);
        setPublished(null);
        setPostBody(null);
        setTitle(null);
        reload?.();
        close?.();
        setErrorsAndWarnings({ errors: {}, warnings: {}, warningsShown: {} });
    };

    const competenciesMapped = downlineCompetencies
        ? downlineCompetencies.map(c => ({ id: c.id, name: `${c.area} - ${c.name}` }))
        : [];

    const footer = (
        <div>
            <SaveButton
                data-testid="post-save-button"
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                onClick={() => save()}
            >
                {SystemText('BlogPost/ManagePost/Buttons/Save')}
            </SaveButton>
            <button className="btn" onClick={() => closeModal()} data-testid="button-post-cancel">
                {SystemText('BlogPost/ManagePost/Buttons/Cancel')}
            </button>
        </div>
    );

    const modalTitleEdit = SystemText('BlogPost/ManagePost/EditTitle');
    const modalTitleAdd = SystemText('BlogPost/ManagePost/AddTitle');

    const modalTitle = postProps?.id ? modalTitleEdit : modalTitleAdd;

    return (
        <Modal
            data-testid="post-modal"
            show={show}
            title={modalTitle}
            close={closeModal}
            footer={footer}
            toggleFormHelp={() => dispatch(toggleFormHelp())}
        >
            <Scroll.Element name="top-of-modal" />
            <div className="form-group" id="manage-task-form">
                <Forms.Text
                    data-testid="field-post-title"
                    value={title}
                    maxLength="100"
                    onChange={value => setTitle(value)}
                    label={SystemText('BlogPost/ManagePost/Fields/Title/Label')}
                    mandatory
                    error={errors.title}
                    help={SystemText('BlogPost/ManagePost/Fields/Title/Help')}
                />
                <Forms.Select
                    data-testid="field-post-competency"
                    value={competencies}
                    onChange={value => setCompetencies(value)}
                    label={SystemText('BlogPost/ManagePost/Fields/Competency/Label')}
                    allowMultiple
                    data={competenciesMapped}
                    warning={warnings.competency_Ids}
                    help={SystemText('BlogPost/ManagePost/Fields/Competency/Help')}
                />
                <Forms.Textarea
                    data-testid="field-post-description"
                    tags={tags}
                    handles={handles}
                    value={description}
                    placeholder={SystemText('BlogPost/ManagePost/Fields/Summary/Placeholder')}
                    onChange={value => setDescription(value)}
                    label={SystemText('BlogPost/ManagePost/Fields/Summary/Label')}
                    rows="3"
                    help={SystemText('BlogPost/ManagePost/Fields/Summary/Help')}
                />
                <Forms.Textarea
                    data-testid="field-post-post"
                    wysiwyg
                    tags={tags}
                    handles={handles}
                    value={postBody}
                    placeholder={SystemText('BlogPost/ManagePost/Fields/Post/Placeholder')}
                    onChange={value => setPostBody(value)}
                    label={SystemText('BlogPost/ManagePost/Fields/Post/Label')}
                    rows="10"
                    help={SystemText('BlogPost/ManagePost/Fields/Post/Help')}
                />
                <Forms.Date
                    data-testid="field-post-date"
                    value={date}
                    help={SystemText('BlogPost/ManagePost/Fields/Date/Help')}
                    onChange={value => setDate(value)}
                    label={SystemText('BlogPost/ManagePost/Fields/Date/Label')}
                    mandatory
                    error={errors.date}
                />
                <Forms.Toggle
                    data-testid="field-post-published"
                    value={published}
                    onChange={value => setPublished(value)}
                    label={SystemText('BlogPost/ManagePost/Fields/Published/Label')}
                    help={SystemText('BlogPost/ManagePost/Fields/Published/Help')}
                />
                <TitleBar
                    data-testid="field-post-bar"
                    title={SystemText('BlogPost/ManagePost/MoreOptions/Title')}
                    openTitle="Less Options"
                    className="showMore"
                >
                    <Forms.Image
                        data-testid="field-post-image"
                        value={blogImage}
                        onChange={value => setBlogImage(value)}
                        onMetaChange={value => setBlogImageMeta(value)}
                        label={SystemText('BlogPost/ManagePost/Fields/Image/Label')}
                        folder="blog_Image"
                        addImage={(folder, src) => dispatch(addImage(folder, src))}
                        config={config}
                        images={images}
                        help={SystemText('BlogPost/ManagePost/Fields/Image/Help')}
                    />
                    <Files
                        data-testid="field-post-attachments"
                        value={assets}
                        onChange={value => setAssets(value)}
                        label={SystemText('BlogPost/ManagePost/Fields/Attachments/Label')}
                        folder={uploadType.blog}
                        allowMultiple
                        modalInModal
                        help={SystemText('BlogPost/ManagePost/Fields/Attachments/Help')}
                    />
                </TitleBar>
            </div>
        </Modal>
    );
};

export default ManagePost;
